import React, { useCallback, useMemo } from 'react';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
/**
 * Type hack to prevent {@link Tippy} from being imported as an object (ES module)
 * in production when it is imported within another component.
 * @see https://github.com/Hacker0x01/react-datepicker/issues/3834#issuecomment-1451662259
 */
const ReactTippy = Tippy.default ?? Tippy;
/**
 * Hide tooltips when they and their children lose focus.
 * @see https://atomiks.github.io/tippyjs/v6/plugins/#hideonpopperblur
 * @type {{defaultValue: boolean, name: string, fn(*=): {onCreate(): void}}}
 */
export const hideOnPopperBlur = {
    name: 'hideOnPopperBlur',
    defaultValue: true,
    fn(instance) {
        return {
            onCreate() {
                instance.popper.addEventListener('focusout', (event) => {
                    if (instance.props.hideOnPopperBlur &&
                        event.relatedTarget &&
                        !instance.popper.contains(event.relatedTarget)) {
                        instance.hide();
                    }
                });
            },
        };
    },
};
/**
 * Close the tooltip when pressing Escape key.
 */
export const closeOnEscape = {
    name: 'closeOnEscape',
    defaultValue: true,
    fn(instance) {
        function onKeyDown(event) {
            if (event.key === 'Escape') {
                instance.hide();
            }
        }
        return {
            onShow() {
                document.addEventListener('keydown', onKeyDown);
            },
            onHide() {
                document.removeEventListener('keydown', onKeyDown);
            },
            onDestroy() {
                document.removeEventListener('keydown', onKeyDown);
            },
        };
    },
};
/**
 * Additional {@link Partial<PopperOptions>} options.
 */
const popperOptions = {
    // [2]
    modifiers: [
        {
            name: 'flip',
            options: {
                /** @TODO: Check position values with {@link TooltipProps.placement}. */
                fallbackPlacements: ['bottom', 'right'],
            },
        },
        {
            name: 'preventOverflow',
            options: {
                altAxis: true,
                tether: false,
            },
        },
    ],
};
/**
 * Bronson [Tooltip](https://bronson.vwfs.io/bluelabel/components/detail/bronson-tooltip.html) component.
 * @desc Tooltips are user-triggered messages that provide additional information about a page element or feature.
 */
export function Tooltip({ alwaysVisible, appendToBody, children, className, content, id, placement = 'bottom', __internalDisableTooltipClass, ...otherProps }) {
    const tooltipId = id || uniqueId('tooltip-');
    const buttonClassNameList = classNames({
        'c-tooltip': !__internalDisableTooltipClass,
    }, className).trim();
    const contentTemplateId = `template-${tooltipId}`;
    const renderChildren = () => {
        const triggerProps = {
            'data-tippy-trigger': alwaysVisible ? 'manual' : undefined,
            'data-tippy-showoncreate': alwaysVisible ? 'true' : undefined,
            'data-tippy-hideonclick': alwaysVisible ? 'false' : undefined,
            'aria-describedby': undefined,
            className: buttonClassNameList,
        };
        if (typeof content === 'string') {
            triggerProps['data-tippy-content'] = content;
        }
        else if (contentTemplateId) {
            triggerProps['data-template'] = contentTemplateId;
        }
        if (typeof children === 'string') {
            return (React.createElement("button", { type: "button", ...triggerProps }, children));
        }
        return React.cloneElement(children, {
            ...triggerProps,
            className: classNames(children.props.className, buttonClassNameList),
        });
    };
    const triggerObj = useMemo(() => {
        if (alwaysVisible) {
            return { trigger: 'manual' };
        }
        return {};
    }, [alwaysVisible]);
    /**
     * Set the `z-index` of the parent element high enough and disable any potential
     * transitioning of it. `z-index: 2001` is inherited from the header setting in
     * {@see packages/bronson-default/src/scss/01-settings/_settings.z-layers.scss}
     * @param {Instance} instance - A tippy instance is an individual tippy object.
     */
    const onTrigger = useCallback((instance) => {
        /**
         * Prevent changes to the tooltip when it was appended to the `document.body`.
         */
        if (!appendToBody) {
            instance?.reference?.parentElement?.style.setProperty('transition-duration', '0s');
            instance?.reference?.parentElement?.style.setProperty('z-index', '2001');
        }
    }, []);
    /**
     * Reset the `z-index` and the `transition-duration` from the {onTrigger} method.
     * @param {Instance} instance - A tippy instance is an individual tippy object.
     */
    const onHidden = useCallback((instance) => {
        /**
         * Prevent changes to the tooltip when it was appended to the `document.body`.
         */
        if (!appendToBody) {
            instance?.reference?.parentElement?.style.removeProperty('z-index');
            instance?.reference?.parentElement?.style.removeProperty('transition-duration');
        }
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(ReactTippy, { allowHTML: true, animation: "default", appendTo: appendToBody ? () => document.body : undefined, aria: {
                content: 'describedby',
                expanded: undefined,
            }, arrow: true, content: content, hideOnClick: !alwaysVisible, interactive: true, onHidden: onHidden, onTrigger: onTrigger, placement: placement, plugins: [hideOnPopperBlur, closeOnEscape], popperOptions: popperOptions, showOnCreate: alwaysVisible, theme: "default", zIndex: appendToBody ? 2001 : undefined, ...triggerObj, ...otherProps }, renderChildren()),
        typeof content !== 'string' && contentTemplateId && React.createElement("template", { id: contentTemplateId }, content)));
}
